import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@fragments';
import { IconSearch } from '@tabler/icons-react';
import { useArtEditorContext } from '../../../context';
import { createIconLayer, createShapeLayer, inkFor } from '../../../../model/factory';
import { ICON_GROUPS, iconComponent, iconLabel } from '../../../../model/icons';
import { SHAPES } from '../../../../model/schema';
import { shapePath } from '../../../../model/shapes';

const PREVIEW = { rect: [40, 28], ellipse: [36, 36], triangle: [36, 32], diamond: [34, 34], star: [36, 36], hexagon: [36, 34], line: [40, 8], arrow: [40, 22] };

export function ShapePreview({ shape }) {
  const [width, height] = PREVIEW[shape];
  return <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
    <path d={shapePath(shape, width, height, { cornerRadius: shape === 'rect' ? 6 : 0 })}
      fill={shape === 'line' ? 'none' : 'currentColor'} stroke={shape === 'line' ? 'currentColor' : 'none'} strokeWidth={shape === 'line' ? 4 : 0} strokeLinecap="round" />
  </svg>;
}

// Shapes and icons. Search filters the icons by name; in `replaceIcon` mode a pick swaps
// the selected icon instead of adding one.
export default function ElementsPanel({ mode, onClose }) {
  const { t } = useTranslation('artEditor');
  const { editor, brandKit, readOnly } = useArtEditorContext();
  const [query, setQuery] = useState('');
  const doc = editor.document;
  const groups = useMemo(() => {
    const value = query.trim().toLowerCase();
    return ICON_GROUPS.map(group => ({ ...group, icons: group.icons.filter(key => !value || iconLabel(key).includes(value)) })).filter(group => group.icons.length);
  }, [query]);
  if (!doc) return null;
  const primary = brandKit?.colors?.find(color => color.role === 'primary')?.hex || '#1f4d3a';
  const ink = inkFor(editor.page);
  const target = mode === 'replaceIcon' && editor.selected[0]?.type === 'icon' ? editor.selected[0] : null;
  const addShape = shape => {
    editor.add(createShapeLayer(doc, shape, shape === 'line' ? { stroke: { color: ink, width: 8 } } : { fill: { type: 'solid', color: primary }, cornerRadius: 24 }));
    onClose?.();
  };
  const pickIcon = key => {
    if (target) { editor.update(target.id, { icon: key }, { key: null }); onClose?.(); return; }
    editor.add(createIconLayer(doc, key, { color: ink }));
    onClose?.();
  };
  return <>
    {!target && <section className="art-panel-section">
      <Text.subheading contrast={6}>{t('shapes')}</Text.subheading>
      <div className="art-tiles">
        {SHAPES.map(shape => <button key={shape} type="button" className="art-tile" disabled={readOnly} aria-label={t(`shape_${shape}`)} title={t(`shape_${shape}`)}
          onClick={() => addShape(shape)}><ShapePreview shape={shape} /></button>)}
      </div>
    </section>}
    <section className="art-panel-section">
      <label className="art-search">
        <IconSearch aria-hidden="true" />
        <input value={query} onChange={event => setQuery(event.target.value)} placeholder={t('iconSearch')} aria-label={t('iconSearch')} />
      </label>
    </section>
    {groups.map(group => <section key={group.group} className="art-panel-section">
      <Text.subheading contrast={6}>{t(`iconGroup_${group.group}`)}</Text.subheading>
      <div className="art-tiles">
        {group.icons.map(key => {
          const Icon = iconComponent(key);
          return <button key={key} type="button" className={`art-tile ${target?.icon === key ? 'is-active' : ''}`} disabled={readOnly} aria-label={iconLabel(key)} title={iconLabel(key)}
            onClick={() => pickIcon(key)}><Icon aria-hidden="true" stroke={1.6} /></button>;
        })}
      </div>
    </section>)}
    {!groups.length && <Text.caption contrast={6}>{t('iconEmpty')}</Text.caption>}
  </>;
}
